import { useState, useCallback, useRef, useEffect } from 'react'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  useMediaQuery,
  IconButton,
  Fade,
} from '@mui/material'
import { expandedProjects as projects } from '../../../../../utils/consumo'
import { useTheme } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import SlideItem from './SlideItem'

const ProjectsComponents = () => {
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery('(max-width:900px)') // Explícitamente menor a 900px
  const allowDirectClick = useMediaQuery(theme.breakpoints.up('md'))

  const [selectedIndex, setSelectedIndex] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const selectedProject = projects[selectedIndex]

  const handleNext = useCallback(() => {
    setSelectedIndex(prev => (prev + 1) % projects.length)
  }, [])

  const handlePrev = useCallback(() => {
    setSelectedIndex(prev => (prev - 1 + projects.length) % projects.length)
  }, [])

  const handleDragEnd = useCallback(
    (event, info) => {
      setIsDragging(false)
      const swipeThreshold = 50
      const swipeVelocityThreshold = 500

      if (
        Math.abs(info.offset.x) > swipeThreshold ||
        Math.abs(info.velocity.x) > swipeVelocityThreshold
      ) {
        if (info.offset.x > 0) {
          handlePrev()
        } else {
          handleNext()
        }
      }
    },
    [handleNext, handlePrev]
  )

  const SliderSection = () => (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        px: 2,
        py: { xs: 3, md: 4 },
        minHeight: { xs: 300, md: 400 },
        overflow: 'visible',
      }}>
      <motion.div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          cursor: isMobile ? 'grab' : 'default',
        }}
        drag={isMobile ? 'x' : false}
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.2}
        onDragStart={() => setIsDragging(true)}
        onDragEnd={handleDragEnd}
        whileTap={{ cursor: 'grabbing' }}>
        <AnimatePresence initial={false} mode="popLayout">
          {(() => {
            const total = projects.length
            const prevIndex = (selectedIndex - 1 + total) % total
            const nextIndex = (selectedIndex + 1) % total

            const visibleSlides = [
              { ...projects[prevIndex], realIndex: prevIndex, position: 'left' },
              {
                ...projects[selectedIndex],
                realIndex: selectedIndex,
                position: 'center',
              },
              { ...projects[nextIndex], realIndex: nextIndex, position: 'right' },
            ]

            return visibleSlides.map(item => {
              const scale = (() => {
                if (item.position === 'center') {
                  return isMobile ? 0.85 : 0.95
                } else {
                  return isMobile ? 0.6 : 0.75
                }
              })()

              const opacity = item.position === 'center' ? 1 : isMobile ? 0.6 : 0.7
              const zIndex = item.position === 'center' ? 3 : 1
              const offsetX =
                item.position === 'left'
                  ? '-100%'
                  : item.position === 'right'
                  ? '100%'
                  : '0%'

              return (
                <motion.div
                  key={item.realIndex}
                  initial={{ opacity: 0, x: offsetX }}
                  animate={{
                    opacity,
                    scale,
                    x: '0%',
                    zIndex,
                  }}
                  exit={{ opacity: 0, x: offsetX }}
                  transition={{
                    type: 'spring',
                    stiffness: 300,
                    damping: 25,
                    opacity: { duration: 0.2 },
                  }}
                  style={{
                    position: 'relative',
                    margin: '0 8px',
                    pointerEvents: isDragging ? 'none' : 'auto',
                  }}>
                  <SlideItem
                    src={item.src}
                    title={item.title}
                    description={item.subtitle_short}
                    after={item.after}
                    isSelected={item.realIndex === selectedIndex}
                    onClick={() => {
                      if (allowDirectClick && !isDragging) {
                        setSelectedIndex(item.realIndex)
                      }
                    }}
                    navigate={() => {
                      if (item.route && !isDragging && allowDirectClick)
                        navigate(item.route)
                    }}
                  />
                </motion.div>
              )
            })
          })()}
        </AnimatePresence>
      </motion.div>

      {/* Indicadores de página para móvil */}
      {isMobile && (
        <Box
          sx={{
            position: 'absolute',
            bottom: -10,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 1,
            zIndex: 4,
            bgcolor: 'rgba(255,255,255,0.9)',
            borderRadius: 2,
            px: 2,
            py: 1,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}>
          {projects.map((_, index) => (
            <Box
              key={index}
              onClick={() => setSelectedIndex(index)}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: index === selectedIndex ? '#4caf50' : 'rgba(0,0,0,0.3)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: index === selectedIndex ? '#388e3c' : 'rgba(0,0,0,0.5)',
                  transform: 'scale(1.2)',
                },
              }}
            />
          ))}
        </Box>
      )}

      {/* Botones de navegación para desktop */}
      {!isMobile && (
        <>
          <Button
            onClick={handlePrev}
            sx={{
              position: 'absolute',
              left: -20,
              top: '50%',
              transform: 'translateY(-50%)',
              minWidth: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.9)',
              color: '#4caf50',
              zIndex: 4,
              '&:hover': {
                bgcolor: 'rgba(255,255,255,1)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              },
            }}>
            ←
          </Button>
          <Button
            onClick={handleNext}
            sx={{
              position: 'absolute',
              right: -20,
              top: '50%',
              transform: 'translateY(-50%)',
              minWidth: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.9)',
              color: '#4caf50',
              zIndex: 4,
              '&:hover': {
                bgcolor: 'rgba(255,255,255,1)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              },
            }}>
            →
          </Button>
        </>
      )}
    </Box>
  )

  const ContentSection = () => {
    // 🎯 Estados para funcionalidad
    const [selectedFeature, setSelectedFeature] = useState(0)
    const contentRef = useRef(null)
    const [canScrollUp, setCanScrollUp] = useState(false)
    const [canScrollDown, setCanScrollDown] = useState(false)
    const isMobileContent = useMediaQuery('(max-width:900px)') // Usar la misma lógica

    // 🔄 Función para detectar si se puede hacer scroll
    const checkScrollability = useCallback(() => {
      if (!contentRef.current) return

      const element = contentRef.current
      const { scrollTop, scrollHeight, clientHeight } = element

      setCanScrollUp(scrollTop > 10) // Margen de 10px para detectar
      setCanScrollDown(scrollTop < scrollHeight - clientHeight - 10)
    }, [])

    // 📜 Funciones de scroll suave
    const scrollDown = () => {
      if (contentRef.current) {
        contentRef.current.scrollBy({
          top: 150, // Scroll de 150px hacia abajo
          behavior: 'smooth',
        })
      }
    }

    const scrollUp = () => {
      if (contentRef.current) {
        contentRef.current.scrollBy({
          top: -150, // Scroll de 150px hacia arriba
          behavior: 'smooth',
        })
      }
    }

    // 👀 Detectar cambios en el scroll
    useEffect(() => {
      const element = contentRef.current
      if (!element) return

      // Verificar scroll inicial
      setTimeout(checkScrollability, 100)

      // Agregar listener de scroll
      element.addEventListener('scroll', checkScrollability)

      // Cleanup
      return () => element.removeEventListener('scroll', checkScrollability)
    }, [checkScrollability, selectedFeature])

    return (
      <Box sx={{ position: 'relative', width: '100%', height: { xs: 'auto', md: 420 } }}>
        {/* 📦 Contenedor con scroll oculto */}
        <Box
          ref={contentRef}
          key={selectedIndex}
          sx={{
            width: '100%',
            height: { xs: 'auto', md: 420 },
            maxHeight: { xs: 'none', md: 420 },
            overflowY: { xs: 'visible', md: 'auto' },
            pr: { md: 1 },

            // 🔧 Ocultar scrollbar pero mantener funcionalidad
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none', // Firefox
            msOverflowStyle: 'none', // IE/Edge

            animation: 'fadeIn 0.4s ease-in-out',
            '@keyframes fadeIn': {
              from: { opacity: 0, transform: 'translateY(10px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
          }}>
          <Typography
            variant="h4"
            component="h2"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              mb: { xs: 1.5, md: 2 },
              fontSize: { xs: '1.3rem', md: '1.7rem' },
              lineHeight: 1.2,
            }}>
            {selectedProject?.title || 'Selecciona un proyecto'}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.secondary,
              mb: { xs: 1.5, md: 2 },
              lineHeight: 1.4,
              fontSize: { xs: '0.9rem', md: '0.95rem' },
              display: '-webkit-box',
              WebkitLineClamp: { xs: 3, md: 4 },
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}>
            {selectedProject?.description || 'Sin descripción disponible.'}
          </Typography>

          {/* 🚀 CARACTERÍSTICAS INTERACTIVAS MEJORADAS */}
          {selectedProject?.features?.length > 0 && (
            <Box sx={{ mb: { xs: 2, md: 2.5 } }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 1.5,
                  fontWeight: 'bold',
                  fontSize: { xs: '0.95rem', md: '1rem' },
                }}>
                Características principales:
              </Typography>

              {/* Chips interactivos */}
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.8,
                  mb: 2,
                }}>
                {selectedProject.features.map((feature, i) => (
                  <Chip
                    key={i}
                    icon={<CheckCircleIcon fontSize="small" />}
                    label={feature}
                    size="small"
                    clickable
                    onClick={() => setSelectedFeature(i)}
                    color={selectedFeature === i ? 'primary' : 'default'}
                    variant={selectedFeature === i ? 'filled' : 'outlined'}
                    sx={{
                      borderRadius: 2,
                      fontWeight: selectedFeature === i ? 600 : 500,
                      fontSize: { xs: '0.75rem', md: '0.8rem' },
                      height: { xs: 28, md: 32 },
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      backgroundColor:
                        selectedFeature === i ? '#4caf50' : 'rgba(76,175,80,0.08)',
                      color: selectedFeature === i ? 'white' : 'inherit',
                      '&:hover': {
                        backgroundColor:
                          selectedFeature === i ? '#388e3c' : 'rgba(76,175,80,0.15)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 2px 8px rgba(76,175,80,0.2)',
                      },
                      '&:active': {
                        transform: 'translateY(0)',
                      },
                    }}
                  />
                ))}
              </Box>

              {/* Descripción de la característica seleccionada */}
              {selectedProject.featuresDescription?.[selectedFeature] && (
                <motion.div
                  key={selectedFeature}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}>
                  <Card
                    elevation={0}
                    sx={{
                      bgcolor: 'rgba(76,175,80,0.05)',
                      border: '1px solid rgba(76,175,80,0.2)',
                      borderRadius: 2,
                      overflow: 'hidden',
                    }}>
                    <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckCircleIcon
                          sx={{
                            color: '#4caf50',
                            fontSize: 18,
                            mt: 0.2,
                            flexShrink: 0,
                          }}
                        />
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              color: '#2e7d32',
                              mb: 0.5,
                              fontSize: { xs: '0.85rem', md: '0.9rem' },
                            }}>
                            {selectedProject.features[selectedFeature]}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'text.secondary',
                              fontSize: { xs: '0.8rem', md: '0.85rem' },
                              lineHeight: 1.4,
                            }}>
                            {selectedProject.featuresDescription[selectedFeature]}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {/* Indicador de interactividad */}
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  textAlign: 'center',
                  color: 'text.disabled',
                  fontSize: '0.7rem',
                  mt: 1,
                  fontStyle: 'italic',
                }}>
                💡 Haz click en cada característica para ver más detalles
              </Typography>
            </Box>
          )}

          {/* Card de impacto más compacta */}
          {selectedProject?.impact && (
            <Card
              elevation={1}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid rgba(147, 192, 50, 0.2)',
                mb: { xs: 1.5, md: 2 },
                borderRadius: '8px',
              }}>
              <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 0.8,
                    color: 'success.main',
                    fontWeight: 600,
                    fontSize: { xs: '0.9rem', md: '1rem' },
                  }}>
                  {selectedProject.titleimpact}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: '0.8rem', md: '0.9rem' },
                    lineHeight: 1.3,
                  }}>
                  {selectedProject.impact}
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Botón de acción - Arreglado para móviles */}
          {selectedProject?.route && (
            <Box
              sx={{
                position: { xs: 'static', md: 'sticky' },
                bottom: { xs: 'auto', md: 0 },
                pt: { xs: 2, md: 1 },
                pb: { xs: 2, md: 1 },
                mt: { xs: 1, md: 0 },
                bgcolor: { xs: 'transparent', md: 'rgba(255, 255, 255, 0.48)' },
                borderRadius: 2,
                backdropFilter: 'blur(2px)',
                px: { md: 1 },
              }}>
              <Button
                variant="contained"
                onClick={() => navigate(selectedProject.route)}
                fullWidth
                sx={{
                  backgroundColor: '#4caf50',
                  fontWeight: 'bold',
                  py: { xs: 1, md: 1.2 },
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
                  fontSize: { xs: '0.9rem', md: '1rem' },
                  '&:hover': {
                    backgroundColor: '#388e3c',
                    boxShadow: '0 4px 12px rgba(56, 142, 60, 0.4)',
                  },
                }}>
                EXPLORAR EL MAPA
              </Button>
            </Box>
          )}

          {/* 📱 Espaciado adicional para móviles - Evitar que se monte el contenido */}
          {isMobileContent && <Box sx={{ height: 60 }} />}
        </Box>

        {/* 🎯 BOTONES DE NAVEGACIÓN FLOTANTES */}
        {/* Botón para bajar */}
        <Fade in={canScrollDown && !isMobileContent}>
          <IconButton
            onClick={scrollDown}
            sx={{
              position: 'absolute',
              bottom: 60,
              right: 8,
              backgroundColor: 'rgba(76, 175, 80, 0.9)',
              color: 'white',
              border: 'none', // Eliminar cualquier borde
              boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
              '&:hover': {
                backgroundColor: 'rgba(56, 142, 60, 0.9)',
                boxShadow: '0 4px 12px rgba(76, 175, 80, 0.4)',
                border: 'none', // Asegurar que no aparezca borde en hover
              },
              '&:focus': {
                border: 'none', // Eliminar borde de focus
                outline: 'none',
              },
              zIndex: 10,
              animation: 'bounce 2s infinite',
              '@keyframes bounce': {
                '0%, 20%, 50%, 80%, 100%': {
                  transform: 'translateY(0)',
                },
                '40%': {
                  transform: 'translateY(-5px)',
                },
                '60%': {
                  transform: 'translateY(-3px)',
                },
              },
            }}>
            <KeyboardArrowDownIcon />
          </IconButton>
        </Fade>

        {/* Botón para subir */}
        <Fade in={canScrollUp && !isMobileContent}>
          <IconButton
            onClick={scrollUp}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: 'rgba(76, 175, 80, 0.9)',
              color: 'white',
              border: 'none', // Eliminar cualquier borde
              boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
              '&:hover': {
                backgroundColor: 'rgba(56, 142, 60, 0.9)',
                boxShadow: '0 4px 12px rgba(76, 175, 80, 0.4)',
                border: 'none', // Asegurar que no aparezca borde en hover
              },
              '&:focus': {
                border: 'none', // Eliminar borde de focus
                outline: 'none',
              },
              zIndex: 10,
            }}>
            <KeyboardArrowUpIcon />
          </IconButton>
        </Fade>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        background: `
          linear-gradient(to bottom,
            transparent 0%,
            rgba(147, 192, 50, 0.07) 5%,
            rgba(147, 192, 50, 0.3) 25%,
            rgba(147, 192, 50, 0.4) 50%,
            rgba(147, 192, 50, 0.3) 75%,
            rgba(147, 192, 50, 0.07) 95%,
            transparent 100%
          )
        `,
        backgroundRepeat: 'no-repeat',
        backgroundSize: { xs: '100% 100%', md: '100% 100%' },
        backgroundPosition: 'left',
      }}>
      <Grid container spacing={0} sx={{ pb: { xs: 2, md: 4 } }}>
        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          lg={7}
          xl={6}
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: { xs: 2, md: 4 },
            py: { xs: 2, md: 3 },
          }}>
          <SliderSection />
        </Grid>

        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          lg={5}
          xl={6}
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: { xs: 3, md: 6 },
            py: { xs: 2, md: 2 },
            bgcolor: '#fafafa70',
          }}>
          <ContentSection />
        </Grid>
      </Grid>
    </Box>
  )
}

export default ProjectsComponents
